import { RootState, selectTokenDecimals, useDappSelector } from "#/redux-state"
import { BridgeableTokens } from "#/shared/types"
import { bigIntToFloat } from "#/shared/utils"

import { useFetchStatistics } from "../statistics"
import { useEstimateTbtcFees } from "../tbtc"
import { useBTCToUSDConversion } from "../token"

import useEstimateBridgeDepositGas from "./useEstimateBridgeDepositGas"
import useEstimateBridgeERC20Gas from "./useEstimateBridgeERC20Gas"

export default function useEstimateBridgeAssetFees(
  token: BridgeableTokens,
  amount?: bigint,
  depositId?: number,
) {
  const { data: statistics } = useFetchStatistics()

  // If the token is `BTC` and `depositId` is available it means that it's not
  // BTC -> Mezo chain BTC flow. In that case we actually bridge `tBTC` from
  // Portal to Mezo chain. The BTC deposit is represented as tBTC in the Portal
  // contract.
  const isBitcoinFlow = token === "BTC" && !depositId

  const bitcoinDecimals = useDappSelector((state: RootState) =>
    selectTokenDecimals(state, "BTC"),
  )

  const bridgeErc20Mutation = useEstimateBridgeERC20Gas(
    token as Exclude<BridgeableTokens, "BTC">,
    amount,
    !isBitcoinFlow && !depositId,
  )

  const bridgeDepositMutation = useEstimateBridgeDepositGas(
    token,
    depositId,
    !isBitcoinFlow && !!depositId,
  )

  const isFetchingEthereumBridgeFee =
    bridgeErc20Mutation.isFetching || bridgeDepositMutation.isFetching

  const estimatedEthereumTransactionFee = depositId
    ? bridgeDepositMutation.estimatedTransactionFee
    : bridgeErc20Mutation.estimatedTransactionFee

  const ethTransactionFeeInUSD =
    Number(bigIntToFloat(estimatedEthereumTransactionFee ?? 0n, 18, 7)) *
    (statistics?.ethPrice ?? 0)

  const { fees: estimatedTbtcFees, isFetching: isFetchingTbtcFees } =
    useEstimateTbtcFees(amount, isBitcoinFlow)

  // TODO: Fetch the native Bitcoin fees.
  const nativeBitcoinFees = 0n

  const bitcoinServiceFeeInUSD = useBTCToUSDConversion(
    bigIntToFloat(estimatedTbtcFees, bitcoinDecimals),
  )

  const btcTransactionFeeInUSD = useBTCToUSDConversion(
    bigIntToFloat(nativeBitcoinFees, bitcoinDecimals),
  )

  const isFetchingBitcoinFees = isFetchingTbtcFees

  return {
    isFetching: isBitcoinFlow
      ? isFetchingBitcoinFees
      : isFetchingEthereumBridgeFee,
    estimatedTransactionFee: isBitcoinFlow
      ? nativeBitcoinFees
      : estimatedEthereumTransactionFee,
    estimatedTransactionFeeInUSD: isBitcoinFlow
      ? btcTransactionFeeInUSD
      : ethTransactionFeeInUSD,
    serviceFee: isBitcoinFlow ? estimatedTbtcFees : 0n,
    serviceFeeInUSD: isBitcoinFlow ? bitcoinServiceFeeInUSD : 0n,
  }
}
